import { Card, CodeViewer, Flex, SimpleTable, Typography } from "djuno-design";

const { Title, Text } = Typography;

const UseWindowOnClickPage = () => {
  return (
    <div style={{ width: "100%", height: "100%" }}>
      <Flex direction="col">
        <Flex direction="col">
          <Title level={4}>useWindowOnClick</Title>
          <Text size="sm" style={{ marginBottom: 30 }}>
            The useWindowOnClick hook allows you to listen for click events on
            the window object, with an optional callback function that is
            executed when the event occurs. Additionally, you can specify
            elements to be ignored (i.e., when the click occurs on these
            elements, the callback will not be triggered). This hook is useful
            for handling global click events, such as closing modals, dropdowns,
            or handling clicks outside specific components.
          </Text>

          <Title level={5}>Usage</Title>
          <Text size="sm" className="mb-3">
            This hook is primarily designed to manage side effects when a click
            event happens on the window. You pass in a callback function, a list
            of dependencies, and optional event listener options. You can also
            specify elements to be ignored when clicked, preventing the callback
            from being triggered.
          </Text>

          <CodeViewer
            language="javascript"
            copyable
            showLineNumbers
            wrapLongLines
            code={`import React, { useRef } from 'react'
import { useWindowOnClick } from 'djuno-design'

const Dropdown = () => {
  const dropdownRef = useRef<HTMLDivElement>(null)

  useWindowOnClick(() => {
    console.log('Clicked outside the dropdown!')
  }, [], { ignore: [dropdownRef.current] })

  return (
    <div ref={dropdownRef}>
      <button>Open Dropdown</button>
      <div>Dropdown content</div>
    </div>
  )
}

export default Dropdown`}
          />

          <Title level={5} className="mt-8">
            Ignoring Specific Elements
          </Title>
          <Text size="sm" className="mb-3">
            The useWindowOnClick hook can be configured to ignore clicks on
            specific elements. This is done by passing an ignore array in the
            options parameter. Each element in the array is checked, and if the
            click occurs inside one of these elements, the callback will not be
            triggered.
          </Text>

          <CodeViewer
            language="javascript"
            copyable
            showLineNumbers
            wrapLongLines
            code={`useWindowOnClick(() => {
  console.log('Clicked outside the ignored element!')
}, [], { ignore: [element1, element2] })`}
          />

          <Title level={5} className="mt-8">
            Event Listener Options
          </Title>
          <Text size="sm" className="mb-2">
            The hook also accepts standard AddEventListenerOptions, such as
            capture, passive, and once, allowing you to fine-tune the behavior
            of the event listener.
          </Text>
          <ul className="marker:text-sky-400 list-disc pl-6 mb-3">
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  capture
                </Text>
                : Whether to trigger the event listener in the capture phase.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  passive
                </Text>
                : If true, indicates that the event listener will not cancel any
                scrolls.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  once
                </Text>
                : If true, the event listener will only be triggered once and
                then removed.
              </Text>
            </li>
          </ul>
          <CodeViewer
            language="javascript"
            copyable
            showLineNumbers
            wrapLongLines
            code={`useWindowOnClick(() => {
  console.log('Window clicked!')
}, [], { once: true, passive: true })`}
          />
        </Flex>

        <Flex direction="col" className="w-full mt-5">
          <Title level={5} className="">
            Component API
          </Title>

          <Flex direction="col" className="gap-1 w-full">
            <Text size="sm">useWindowOnClick(callbackFn, deps, options)</Text>
            <Card id="API">
              <SimpleTable>
                <SimpleTable.Head>
                  <SimpleTable.TH>Prop Name</SimpleTable.TH>
                  <SimpleTable.TH>Type</SimpleTable.TH>
                  <SimpleTable.TH>Default</SimpleTable.TH>
                  <SimpleTable.TH>Description</SimpleTable.TH>
                </SimpleTable.Head>
                <SimpleTable.Body>
                  {/* callbackFn */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">callbackFn</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        {`(ev: MouseEvent) => any`}
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">false </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        A function to be executed when a click event occurs
                        outside of the ignored elements.
                      </Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* deps */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">deps</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        {`React.DependencyList`}
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">undefined </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Dependency list for the useEffect hook. This determines
                        when the event listener should be reattached.
                      </Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* options */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">options</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        {`AddEventListenerOptions & { ignore?: Element[] }`}
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">undefined </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Optional object with event listener options (e.g.,
                        capture, passive, once) and an array of elements to
                        ignore clicks on.
                      </Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                </SimpleTable.Body>
              </SimpleTable>
            </Card>
          </Flex>
        </Flex>

        <Flex direction="col" className="w-full mt-5">
          <Title level={5} className="">
            Return Value
          </Title>
          <Flex direction="col" className="gap-1 w-full">
            <Text size="sm">
              This hook does not return any values. It sets up a side effect to
              handle click events on the window object and cleans up the
              listener when the component unmounts or dependencies change.
            </Text>
          </Flex>
        </Flex>

        <Flex direction="col" className="w-full mt-5">
          <Title level={5} className="">
            Example Usage
          </Title>
          <CodeViewer
            language="javascript"
            copyable
            showLineNumbers
            wrapLongLines
            code={`useWindowOnClick((event) => {
  console.log('Window clicked:', event)
}, [], { ignore: [dropdownElement], once: true })`}
          />
          <Text size="sm" className="mt-2">
            Here, the hook logs the click event but ignores clicks on the
            dropdownElement. Additionally, it only triggers once due to the once
            option being set.
          </Text>
        </Flex>
      </Flex>
    </div>
  );
};

export default UseWindowOnClickPage;
